// auth.js
// Authentication functions for coaches and athletes

import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc, query, collection, where, getDocs } from 'firebase/firestore';
import { auth, db } from './firebase-config';

/**
 * Generate a memorable coach code
 * Format: [fitness-word][number]
 * Examples: tempo47, gains23, beast92
 */
const generateCoachCode = async () => {
  const fitnessWords = [
    'tempo', 'gains', 'beast', 'iron', 'power', 
    'maxout', 'grind', 'hustle', 'pump', 'flex',
    'reps', 'burn', 'lift', 'peak', 'zone',
    'strong', 'fit', 'active', 'train', 'coach'
  ];
  
  // Try to generate a unique code
  for (let attempt = 0; attempt < 10; attempt++) {
    const word = fitnessWords[Math.floor(Math.random() * fitnessWords.length)];
    const number = Math.floor(Math.random() * 100); // 0-99
    const code = `${word}${number}`;
    
    // Check if this code already exists
    const codesQuery = query(
      collection(db, 'users'),
      where('coach_code', '==', code)
    );
    const snapshot = await getDocs(codesQuery);
    
    if (snapshot.empty) {
      return code; // Unique code found!
    }
  }
  
  // Fallback: use timestamp-based code
  return `coach${Date.now() % 10000}`;
};

/**
 * Find coach by their coach code
 * @param {string} coachCode - The coach's code (e.g., "tempo47")
 * @returns {object} Coach's UID if found
 */
export const findCoachByCode = async (coachCode) => {
  try {
    const codesQuery = query(
      collection(db, 'users'),
      where('coach_code', '==', coachCode.toLowerCase().trim()),
      where('role', '==', 'coach')
    );
    const snapshot = await getDocs(codesQuery);
    
    if (snapshot.empty) {
      return { success: false, error: 'Coach code not found' };
    }
    
    const coachDoc = snapshot.docs[0];
    return {
      success: true,
      coach: {
        uid: coachDoc.id,
        ...coachDoc.data()
      }
    };
  } catch (error) {
    console.error('Error finding coach:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Login user (coach or athlete)
 * @param {string} email 
 * @param {string} password 
 */
export const login = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Get user data from Firestore
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    
    if (userDoc.exists()) {
      return {
        success: true,
        user: {
          uid: user.uid,
          email: user.email,
          ...userDoc.data()
        }
      };
    } else {
      return {
        success: false,
        error: 'User profile not found'
      };
    }
  } catch (error) {
    console.error('Login error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Register a new athlete
 * @param {string} email 
 * @param {string} password 
 * @param {string} name 
 * @param {string} coachCodeOrId - Coach's code (e.g., "tempo47") or UID
 */
export const registerAthlete = async (email, password, name, coachCodeOrId) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Try to find coach by code first (if it's a short code)
    let coachUid = coachCodeOrId;
    if (coachCodeOrId && coachCodeOrId.length < 20) {
      // Likely a coach code, not a UID
      const coachResult = await findCoachByCode(coachCodeOrId);
      if (coachResult.success) {
        coachUid = coachResult.coach.uid;
        console.log('✅ Found coach by code:', coachCodeOrId, '→', coachUid);
      } else {
        // Coach code not found
        return {
          success: false,
          error: `Coach code "${coachCodeOrId}" not found. Please check with your coach.`
        };
      }
    }
    
    // Create user profile in Firestore
    await setDoc(doc(db, 'users', user.uid), {
      email: email,
      name: name,
      role: 'athlete',
      coach_id: coachUid,
      created_at: new Date(),
      active: true
    });
    
    return {
      success: true,
      user: {
        uid: user.uid,
        email: email,
        name: name,
        role: 'athlete',
        coach_id: coachUid
      }
    };
  } catch (error) {
    console.error('Registration error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Register a new coach
 * @param {string} email 
 * @param {string} password 
 * @param {string} name 
 */
export const registerCoach = async (email, password, name) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Generate unique coach code
    const coachCode = await generateCoachCode();
    console.log('✅ Generated coach code:', coachCode);
    
    // Create coach profile in Firestore
    await setDoc(doc(db, 'users', user.uid), {
      email: email,
      name: name,
      role: 'coach',
      coach_code: coachCode,
      created_at: new Date(),
      active: true
    });
    
    return {
      success: true,
      user: {
        uid: user.uid,
        email: email,
        name: name,
        role: 'coach',
        coach_code: coachCode
      }
    };
  } catch (error) {
    console.error('Registration error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Logout current user
 */
export const logout = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error) {
    console.error('Logout error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Send password reset email
 * @param {string} email 
 */
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return {
      success: true,
      message: 'Password reset email sent'
    };
  } catch (error) {
    console.error('Password reset error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get current user data
 */
export const getCurrentUser = async () => {
  const user = auth.currentUser;
  
  if (!user) {
    return null;
  }
  
  try {
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    
    if (userDoc.exists()) {
      return {
        uid: user.uid,
        email: user.email,
        ...userDoc.data()
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user:', error);
    return null;
  }
};

/**
 * Listen to authentication state changes
 * @param {function} callback - Called when auth state changes
 */
export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, async (user) => {
    if (user) {
      const userData = await getCurrentUser();
      callback(userData);
    } else {
      callback(null);
    }
  });
};

/**
 * Check if user is a coach
 * @param {object} user 
 */
export const isCoach = (user) => {
  return user && user.role === 'coach';
};

/**
 * Check if user is an athlete
 * @param {object} user 
 */
export const isAthlete = (user) => {
  return user && user.role === 'athlete';
};
